import { getActualUnitPrice } from '@jane/shared/util';

import type { MoeEvent } from './moengageEvents.types';
import type {
  MoeAddToBagProps,
  MoeCheckoutProps,
  MoeLinkedFirstBankAccountProps,
} from './moengageHelpers.types';

/**
 * As our Moe tracking library grows, the various even helpers should be pulled into separate files
 * and imported into this one.
 */

export const moeAddToBag = ({
  cartUuid,
  product,
  storeId,
  storeName,
}: MoeAddToBagProps): MoeEvent => {
  const pricePerUnit = getActualUnitPrice(product, product.price_id) || 0;
  const priceTotal = pricePerUnit * product.count;

  return {
    name: 'Add to Bag',
    properties: {
      'Brand Id': product.product_brand_id ?? null,
      'Brand Name': product.brand ?? null,
      'Cart Uuid': cartUuid ?? null,
      'Has Brand Discount': product.has_brand_discount ?? false,
      'Price Per Unit': pricePerUnit,
      'Price Total': priceTotal,
      'Product Category': product.category ?? null,
      'Product Id': product.id ?? product.product_id,
      'Product Lineage': product.category,
      'Product Name': product.name,
      'Product Aggregate Rating': product.aggregate_rating ?? null,
      'Product Review Count': product.review_count ?? null,
      Quantity: product.count,
      'Store Id': storeId,
      'Store Name': storeName,
    },
  };
};

export const moeCheckout = ({
  cart,
  janePayStore,
  paymentType,
  storeId,
  storeName,
  reservationTime,
}: MoeCheckoutProps): MoeEvent => {
  const priceTotal = cart.products.reduce<number>((acc, product) => {
    const unitPrice = getActualUnitPrice(product, product.price_id) || 0;
    return acc + product.count * unitPrice;
  }, 0);

  const productCount = cart.products.reduce<number>(
    (acc, product) => acc + product.count,
    0
  );

  const cartFees =
    (cart.service_fee_amount ?? 0) + (cart.delivery_fee_amount ?? 0);

  return {
    name: 'Checkout',
    properties: {
      'Brand Discount Total': cart.brand_discounts_total_with_fee ?? null,
      'Brand Ids': cart.products.map((p) => p.product_brand_id ?? null),
      'Brand Names': cart.products.map((p) => p.brand),
      'Cart Discount Total': cart.cart_discount_amount ?? null,
      'Cart Fees': cartFees,
      'Cart Uuid': cart.uuid ?? null,
      'Cart Item Count': cart.products.length,
      'Cart Subtotal': cart.item_total ?? null,
      'Cart Taxes': cart.sales_tax_amount,
      'Cart Tip': cart.tip_amount,
      'Cart Total': cart.order_total ?? priceTotal,
      'Jane Pay Store': janePayStore,
      'Payment Type': paymentType,
      'Product Count': productCount,
      'Product Categories': cart.products.map((p) => p.category),
      'Product Ids': cart.products.map((p) => p.id ?? p.product_id),
      'Product Lineages': cart.products.map((p) => p.category),
      'Product Names': cart.products.map((p) => p.name),
      'Product Aggregate Ratings': cart.products.map(
        (p) => p.aggregate_rating ?? null
      ),
      'Product Review Counts': cart.products.map((p) => p.review_count ?? null),
      'Reservation Mode': cart.reservation_mode,
      'Reservation Time': reservationTime ?? null,
      'Store Id': storeId,
      'Store Name': storeName,
    },
  };
};

export const moeLinkedFirstBankAccount = ({
  email,
  isGold,
  isJanePay,
  source,
  username,
}: MoeLinkedFirstBankAccountProps): MoeEvent => {
  const eventSource = source
    ? `${source}${isJanePay ? ' (Pay)' : ' (Gold)'}`
    : null;

  return {
    name: 'Linked First Bank Account',
    properties: {
      Email: email,
      isGold,
      Source: eventSource,
      Username: username,
    },
  };
};
